import Skeleton from '@material-ui/lab/Skeleton';
import React, { useCallback, useEffect, useState } from 'react';

import Translation from '@swf/components/Translation/Translation';
import useRequest from '@swf/core/hooks/useRequest';
import type { Article, ArticlesListMeta, Category } from '@swf/help';
import { ArticleList, SkeletonList } from '@swf/help';

import { getCategoryTitle, getSideNavs } from '../../utils';
import Breadcrumb, { BreadcrumbSkeleton } from '../Breadcrumb/Breadcrumb';
import HelpCenterSEO from '../HelpCenterSEO/HelpCenterSEO';
import LoadMore from '../LoadMore/LoadMore';
import SideBar, { SideBarSkeleton } from '../SideBar/SideBar';

import {
  TXTWCL_All_help_categories,
  TXTWCL_OopsX_Page_not_found
} from './CategoryArticleList.messages.json';
import styles from './CategoryArticleList.scss';

interface Props {
  categories: Category[];
  categoryArticles: Article[];
  pageSize: string;
  categoryId: string;
  categoryArticlesMeta?: ArticlesListMeta;
}

const CategoryArticleList: React.FC<Props> = ({
  categories,
  categoryArticles,
  categoryArticlesMeta,
  pageSize,
  categoryId
}) => {
  const [articles, setArticles] = useState(categoryArticles);
  const count = categoryArticlesMeta?.count || 0;
  const cid = categoryArticlesMeta?.category || '';

  const categoryTitle = getCategoryTitle(categories, cid);
  const sideNavs = getSideNavs(categories);
  const request = useRequest();

  useEffect(() => {
    setArticles(categoryArticles);
  }, [categoryArticles]);

  const loadMore = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      const pagesCount = Math.ceil(articles.length / parseInt(pageSize, 10)) + 1;

      request({
        service: `help.articlesByCategoryId`,
        body: { category: categoryId, page: pagesCount, size: pageSize }
      }).subscribe(({ body: { articles: newArticles } }) => {
        setArticles([...articles, ...newArticles]);
      });
    },
    [articles, categoryId, pageSize, request]
  );

  return (
    <>
      {categoryTitle && <HelpCenterSEO title={categoryTitle} />}
      <section>
        <div className={styles.inner}>
          <Breadcrumb id={categoryId} title={categoryTitle} />
          <h1 className={styles.title}>{categoryTitle}</h1>
          <div className={styles.contentWrapper}>
            <div className={styles.content}>
              {articles.length ? (
                <>
                  <ArticleList articles={articles} regularHeight />
                  <LoadMore len={articles.length} count={count} loadMore={loadMore} />
                </>
              ) : (
                !categoryTitle && <Translation tagName="h1" {...TXTWCL_OopsX_Page_not_found} />
              )}
            </div>
            <div className={styles.sideBar}>
              <SideBar
                sideNavs={sideNavs}
                title={<Translation {...TXTWCL_All_help_categories} />}
                categoryId={categoryId}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export const CategoryArticleSkeleton = () => (
  <section>
    <div className={styles.inner}>
      <BreadcrumbSkeleton />
      <Skeleton variant="rect" width={250} height={45} className={styles.title} />
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <SkeletonList
            skeletonSizes={['30%', '35%', '70%', '60%', '25%', '65%', '45%', '85%']}
            className={styles.categoryListSkeleton}
          />
        </div>
        <div className={styles.sideBar}>
          <SideBarSkeleton />
        </div>
      </div>
    </div>
  </section>
);

export default CategoryArticleList;
